import React, { useState, useEffect } from 'react';
import './Approach.css';

const modalities = [
  {
    label: 'Cognitive Behavioral (CBT)',
    definition: 'Identifies and reshapes negative thought patterns and behaviors that fuel emotional distress, building practical skills to respond to life\'s challenges more effectively.',
  },
  {
    label: 'Brainspotting',
    definition: 'Uses specific eye positions to locate and release trauma stored deep in the subcortical brain — a powerful, body-based approach to healing what talk therapy alone may not reach.',
  },
  {
    label: 'EMDR',
    definition: 'Eye Movement Desensitization and Reprocessing uses guided bilateral stimulation to help the brain fully process distressing memories, reducing their emotional charge.',
  },
  {
    label: 'Existential Therapy',
    definition: 'Explores the core questions of human existence — meaning, freedom, isolation, and mortality — to help you live with greater clarity, purpose, and authenticity.',
  },
  {
    label: 'Person-Centered',
    definition: 'Rooted in unconditional positive regard, this approach trusts that you are the expert on your own life. The therapist\'s role is to provide a safe space for self-discovery and growth.',
  },
  {
    label: 'Humanistic',
    definition: 'Honors your inherent worth and capacity for growth, focusing on present experience and helping you move toward your fullest potential rather than analyzing the past.',
  },
  {
    label: 'Culturally Sensitive',
    definition: 'Weaves awareness of cultural identity, background, language, and values into every aspect of care — ensuring therapy feels relevant, respectful, and truly yours.',
  },
  {
    label: 'Strength-Based',
    definition: 'Shifts the focus from what\'s wrong to what\'s working — identifying your existing strengths, resilience, and resources as the foundation for lasting change.',
  },
  {
    label: 'Solution-Focused (SFBT)',
    definition: 'A future-oriented, goal-directed approach that asks: "What does life look like when things are better?" — then builds concrete steps toward that vision.',
  },
  {
    label: 'Positive Psychology',
    definition: 'Draws on research into well-being, flow, and human flourishing to cultivate strengths, gratitude, and positive emotions alongside healing.',
  },
  {
    label: 'Spiritual Psychotherapy',
    definition: 'Honors the role of spirituality, personal belief, and meaning-making in mental health — integrating your inner life into the healing journey without imposing any tradition.',
  },
  {
    label: 'Internal Family System (IFS)',
    definition: 'Views the mind as a system of distinct "parts" — each with its own perspective and role. IFS helps you understand, heal, and harmonize your inner world with compassion.',
  },
];

const Approach = () => {
  const [active, setActive] = useState(null);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') setActive(null);
    };
    if (active) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [active]);

  return (
    <div className="approach">
      <p className="approach-label">How I Work</p>
      <h2 className="approach-heading">Therapeutic Approach</h2>
      <hr className="approach-rule" />
      <p className="approach-intro">
        Every person deserves care that honors their unique background, values, and goals. Anna's practice integrates
        the most effective, research-supported methods within a deeply human, person-centered framework.
      </p>

      <div className="modalities-grid">
        {modalities.map((m) => (
          <button
            key={m.label}
            className="modality-pill"
            onClick={() => setActive(m)}
            aria-expanded={active?.label === m.label}
          >
            {m.label}
          </button>
        ))}
      </div>

      {active && (
        <div className="modality-overlay" onClick={() => setActive(null)} role="dialog" aria-modal="true">
          <div className="modality-popup" onClick={(e) => e.stopPropagation()}>
            <button className="modality-popup-close" onClick={() => setActive(null)} aria-label="Close">✕</button>
            <h3 className="modality-popup-title">{active.label}</h3>
            <p className="modality-popup-body">{active.definition}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Approach;